(function () {
  const MIN_ZOOM = 1;
  const MAX_ZOOM = 4;
  const ZOOM_STEP = 0.25;

  function initPpv2Modal(root) {
    if (!root) return;

    const modal = root.querySelector('[data-ppv2-modal]');
    if (!modal || modal.dataset.ppv2ModalInit === 'true') return;

    const mediaMain = root.querySelector('.ppv2-mediaMain');
    const mainImg = root.querySelector('[data-ppv2-main]');
    const modalContent = modal.querySelector('.ppv2-imageModal__content');
    const modalFrame = modal.querySelector('.ppv2-imageModal__frame');
    const modalImg = modal.querySelector('[data-ppv2-modal-img]');
    const modalCaption = modal.querySelector('[data-ppv2-modal-caption]');
    const closeEls = Array.from(modal.querySelectorAll('[data-ppv2-modal-close]'));
    const dragHandle = modal.querySelector('[data-ppv2-modal-drag]');
    const zoomOutBtn = modal.querySelector('[data-ppv2-zoom-out]');
    const zoomInBtn = modal.querySelector('[data-ppv2-zoom-in]');
    const zoomResetBtn = modal.querySelector('[data-ppv2-zoom-reset]');
    const titleEl = root.querySelector('.ppv2-sidebarTitle');

    if (!mediaMain || !mainImg || !modalContent || !modalFrame || !modalImg) return;

    modal.dataset.ppv2ModalInit = 'true';

    let lastFocusedElement = null;
    let dragState = null;
    let panState = null;
    let zoom = MIN_ZOOM;
    let panX = 0;
    let panY = 0;

    const clamp = (value, min, max) => Math.max(min, Math.min(max, value));
    const getTitle = () => (titleEl ? titleEl.textContent.trim() : '');

    const setModalOffset = (x, y) => {
      modalContent.style.setProperty('--ppv2-modal-offset-x', `${x}px`);
      modalContent.style.setProperty('--ppv2-modal-offset-y', `${y}px`);
    };

    const resetModalOffset = () => {
      setModalOffset(0, 0);
    };

    const getPanBounds = (nextZoom) => {
      const frameRect = modalFrame.getBoundingClientRect();
      const baseWidth = modalImg.offsetWidth;
      const baseHeight = modalImg.offsetHeight;

      if (!frameRect.width || !frameRect.height || !baseWidth || !baseHeight) {
        return { maxX: 0, maxY: 0 };
      }

      return {
        maxX: Math.max(0, (baseWidth * nextZoom - frameRect.width) / 2),
        maxY: Math.max(0, (baseHeight * nextZoom - frameRect.height) / 2),
      };
    };

    const clampPan = (nextX, nextY, nextZoom) => {
      const bounds = getPanBounds(nextZoom);
      return {
        x: clamp(nextX, -bounds.maxX, bounds.maxX),
        y: clamp(nextY, -bounds.maxY, bounds.maxY),
      };
    };

    const applyImageTransform = () => {
      modalImg.style.setProperty('--ppv2-image-scale', `${zoom}`);
      modalImg.style.setProperty('--ppv2-image-pan-x', `${panX}px`);
      modalImg.style.setProperty('--ppv2-image-pan-y', `${panY}px`);

      modalContent.classList.toggle('is-zoomed', zoom > MIN_ZOOM);
      if (zoom <= MIN_ZOOM) {
        modalContent.classList.remove('is-panning');
      }

      if (zoomOutBtn) zoomOutBtn.disabled = zoom <= MIN_ZOOM;
      if (zoomInBtn) zoomInBtn.disabled = zoom >= MAX_ZOOM;
      if (zoomResetBtn) {
        zoomResetBtn.textContent = `${Math.round(zoom * 100)}%`;
        zoomResetBtn.disabled = zoom <= MIN_ZOOM;
      }
    };

    const resetZoom = () => {
      zoom = MIN_ZOOM;
      panX = 0;
      panY = 0;
      panState = null;
      applyImageTransform();
    };

    const setZoom = (nextZoom, anchorClientX, anchorClientY) => {
      const clampedZoom = clamp(Math.round(nextZoom * 100) / 100, MIN_ZOOM, MAX_ZOOM);
      if (Math.abs(clampedZoom - zoom) < 0.001) {
        if (clampedZoom <= MIN_ZOOM && (panX !== 0 || panY !== 0)) {
          resetZoom();
        }
        return;
      }

      let nextPanX = panX;
      let nextPanY = panY;
      const ratio = clampedZoom / zoom;

      if (anchorClientX != null && anchorClientY != null) {
        const frameRect = modalFrame.getBoundingClientRect();
        const centerX = frameRect.left + frameRect.width / 2;
        const centerY = frameRect.top + frameRect.height / 2;
        nextPanX = panX * ratio + (1 - ratio) * (anchorClientX - centerX);
        nextPanY = panY * ratio + (1 - ratio) * (anchorClientY - centerY);
      } else {
        nextPanX = panX * ratio;
        nextPanY = panY * ratio;
      }

      zoom = clampedZoom;

      if (zoom <= MIN_ZOOM) {
        panX = 0;
        panY = 0;
      } else {
        const nextPan = clampPan(nextPanX, nextPanY, zoom);
        panX = nextPan.x;
        panY = nextPan.y;
      }

      applyImageTransform();
    };

    const syncPanBounds = () => {
      if (zoom <= MIN_ZOOM) {
        panX = 0;
        panY = 0;
      } else {
        const nextPan = clampPan(panX, panY, zoom);
        panX = nextPan.x;
        panY = nextPan.y;
      }
      applyImageTransform();
    };

    const closeModal = () => {
      modalContent.classList.remove('is-dragging');
      modalContent.classList.remove('is-panning');
      dragState = null;
      panState = null;
      resetZoom();
      resetModalOffset();
      modal.classList.remove('is-open');
      modal.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('ppv2-modal-open');
      if (lastFocusedElement && typeof lastFocusedElement.focus === 'function') {
        lastFocusedElement.focus();
      }
    };

    const closeAnyOpenModal = () => {
      document.querySelectorAll('.ppv2-imageModal.is-open').forEach((openModalEl) => {
        openModalEl.classList.remove('is-open');
        openModalEl.setAttribute('aria-hidden', 'true');
        const openModalContent = openModalEl.querySelector('.ppv2-imageModal__content');
        if (openModalContent) {
          openModalContent.classList.remove('is-dragging');
          openModalContent.classList.remove('is-panning');
          openModalContent.style.setProperty('--ppv2-modal-offset-x', '0px');
          openModalContent.style.setProperty('--ppv2-modal-offset-y', '0px');
        }
      });
      document.body.classList.remove('ppv2-modal-open');
    };

    const openModal = () => {
      const src = mainImg.currentSrc || mainImg.getAttribute('src');
      if (!src) return;

      closeAnyOpenModal();
      lastFocusedElement = document.activeElement;
      modalImg.setAttribute('src', src);
      modalImg.setAttribute('alt', mainImg.getAttribute('alt') || getTitle());

      if (modalCaption) {
        modalCaption.textContent = getTitle() || mainImg.getAttribute('alt') || '';
      }

      modalContent.classList.remove('is-dragging');
      modalContent.classList.remove('is-panning');
      resetZoom();
      resetModalOffset();
      modal.classList.add('is-open');
      modal.setAttribute('aria-hidden', 'false');
      document.body.classList.add('ppv2-modal-open');
      modalContent.focus();
    };

    const updateDragPosition = (clientX, clientY) => {
      if (!dragState) return;
      const rect = modalContent.getBoundingClientRect();
      const maxX = Math.max(0, (window.innerWidth - rect.width) / 2 - 12);
      const maxY = Math.max(0, (window.innerHeight - rect.height) / 2 - 12);
      const nextX = dragState.originX + (clientX - dragState.startX);
      const nextY = dragState.originY + (clientY - dragState.startY);
      const clampedX = Math.max(-maxX, Math.min(maxX, nextX));
      const clampedY = Math.max(-maxY, Math.min(maxY, nextY));
      setModalOffset(clampedX, clampedY);
    };

    const onDragMove = (event) => {
      updateDragPosition(event.clientX, event.clientY);
    };

    const onDragEnd = () => {
      modalContent.classList.remove('is-dragging');
      dragState = null;
      document.removeEventListener('pointermove', onDragMove);
      document.removeEventListener('pointerup', onDragEnd);
      document.removeEventListener('pointercancel', onDragEnd);
    };

    const onPanMove = (event) => {
      if (!panState) return;
      const nextPan = clampPan(
        panState.originX + (event.clientX - panState.startX),
        panState.originY + (event.clientY - panState.startY),
        zoom
      );
      panX = nextPan.x;
      panY = nextPan.y;
      applyImageTransform();
    };

    const onPanEnd = () => {
      modalContent.classList.remove('is-panning');
      panState = null;
      document.removeEventListener('pointermove', onPanMove);
      document.removeEventListener('pointerup', onPanEnd);
      document.removeEventListener('pointercancel', onPanEnd);
    };

    mediaMain.addEventListener('click', function (event) {
      if (event.target.closest('[data-ppv2-media-prev], [data-ppv2-media-next]')) return;
      openModal();
    });

    closeEls.forEach(function (el) {
      el.addEventListener('click', closeModal);
    });

    modal.addEventListener('click', function (event) {
      if (!modal.classList.contains('is-open')) return;
      if (event.target.closest('.ppv2-imageModal__content')) return;
      closeModal();
    });

    modalImg.addEventListener('load', syncPanBounds);

    if (zoomOutBtn) {
      zoomOutBtn.addEventListener('click', function () {
        setZoom(zoom - ZOOM_STEP);
      });
    }

    if (zoomInBtn) {
      zoomInBtn.addEventListener('click', function () {
        setZoom(zoom + ZOOM_STEP);
      });
    }

    if (zoomResetBtn) {
      zoomResetBtn.addEventListener('click', function () {
        resetZoom();
      });
    }

    modalFrame.addEventListener(
      'wheel',
      function (event) {
        if (!modal.classList.contains('is-open')) return;
        event.preventDefault();
        const direction = event.deltaY < 0 ? ZOOM_STEP : -ZOOM_STEP;
        setZoom(zoom + direction, event.clientX, event.clientY);
      },
      { passive: false }
    );

    modalFrame.addEventListener('dblclick', function (event) {
      if (!modal.classList.contains('is-open')) return;
      event.preventDefault();
      if (zoom > MIN_ZOOM) {
        resetZoom();
        return;
      }
      setZoom(2, event.clientX, event.clientY);
    });

    modalFrame.addEventListener('pointerdown', function (event) {
      if (!modal.classList.contains('is-open')) return;
      if (zoom <= MIN_ZOOM || event.button !== 0) return;
      event.preventDefault();
      panState = {
        startX: event.clientX,
        startY: event.clientY,
        originX: panX,
        originY: panY,
      };
      modalContent.classList.add('is-panning');
      document.addEventListener('pointermove', onPanMove);
      document.addEventListener('pointerup', onPanEnd);
      document.addEventListener('pointercancel', onPanEnd);
    });

    modalContent.addEventListener('keydown', function (event) {
      if (!modal.classList.contains('is-open')) return;
      if (event.key === '+' || event.key === '=') {
        event.preventDefault();
        setZoom(zoom + ZOOM_STEP);
        return;
      }
      if (event.key === '-' || event.key === '_') {
        event.preventDefault();
        setZoom(zoom - ZOOM_STEP);
        return;
      }
      if (event.key === '0') {
        event.preventDefault();
        resetZoom();
      }
    });

    if (dragHandle) {
      dragHandle.addEventListener('pointerdown', function (event) {
        if (event.target.closest('button')) return;
        if (event.button !== 0) return;
        event.preventDefault();
        const offsetX = parseFloat(modalContent.style.getPropertyValue('--ppv2-modal-offset-x')) || 0;
        const offsetY = parseFloat(modalContent.style.getPropertyValue('--ppv2-modal-offset-y')) || 0;
        dragState = {
          startX: event.clientX,
          startY: event.clientY,
          originX: offsetX,
          originY: offsetY,
        };
        modalContent.classList.add('is-dragging');
        document.addEventListener('pointermove', onDragMove);
        document.addEventListener('pointerup', onDragEnd);
        document.addEventListener('pointercancel', onDragEnd);
      });
    }

    if (!document.body.dataset.ppv2ModalEscapeListener) {
      document.body.dataset.ppv2ModalEscapeListener = 'true';
      document.addEventListener('keydown', function (event) {
        if (event.key !== 'Escape') return;
        closeAnyOpenModal();
      });
    }

    window.addEventListener('resize', syncPanBounds);
    applyImageTransform();
  }

  function initAllPpv2Modals() {
    document.querySelectorAll('.ppv2').forEach(initPpv2Modal);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAllPpv2Modals);
  } else {
    initAllPpv2Modals();
  }

  document.addEventListener('product-info:loaded', function (event) {
    const root = event.target.querySelector('.ppv2');
    if (root) initPpv2Modal(root);
  });
})();
